import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { Search, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { formatDuration } from '../utils';

export default function AdminCalls() {
  const navigate = useNavigate();
  const toast = useToast();
  const [calls, setCalls] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [intent, setIntent] = useState('');
  const [minLead, setMinLead] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/admin/companies?limit=200').then(({ data }) => setCompanies(data.companies)).catch(() => {});
  }, []);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (companyFilter) params.set('company_id', companyFilter);
      if (intent) params.set('intent', intent);
      if (minLead) params.set('min_score', minLead);
      const { data } = await api.get(`/api/calls?${params}`);
      setCalls(data.calls);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error(err);
      toast.error('Kunne ikke laste samtaler');
    } finally {
      setLoading(false);
    }
  }, [page, search, companyFilter, intent, minLead, toast]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const companyMap = {};
  companies.forEach(c => { companyMap[c._id] = c.name; });

  const fmt = (d) => new Date(d).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  // formatDuration imported from utils

  return (
    <div data-testid="admin-calls-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Alle samtaler</h1>
        <p className="text-slate-500 text-sm mt-1">{total} samtaler på tvers av alle bedrifter</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="admin-call-search"
            type="text"
            placeholder="Søk etter navn, nummer..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue"
          />
        </div>
        <select
          data-testid="admin-company-filter"
          value={companyFilter}
          onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue"
        >
          <option value="">Alle bedrifter</option>
          {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select value={intent} onChange={(e) => { setIntent(e.target.value); setPage(1); }}
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
          <option value="">Alle henvendelser</option>
          <option value="Booking">Booking</option>
          <option value="Forespørsel">Forespørsel</option>
          <option value="Prisforespørsel">Prisforespørsel</option>
          <option value="Klage">Klage</option>
          <option value="Akutt">Akutt</option>
        </select>
        <select value={minLead} onChange={(e) => { setMinLead(e.target.value); setPage(1); }}
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
          <option value="">Alle lead-scorer</option>
          <option value="80">80+ (varme)</option>
          <option value="50">50+</option>
          <option value="30">30+</option>
        </select>
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-calls-table">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Bedrift</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Innringer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Telefon</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Henvendelse</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Lead</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Haster</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Varighet</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Tidspunkt</th>
              </tr>
            </thead>
            <tbody>
              {loading ? [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">{[...Array(8)].map((_, j) => <td key={j} className="px-4 py-4"><div className="h-4 bg-navy-700 rounded w-16" /></td>)}</tr>
              )) : calls.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-slate-500">Ingen samtaler funnet</td></tr>
              ) : calls.map(call => {
                const _ca = call.call_analysis || {}; const a = { ..._ca, ...(_ca.custom_analysis_data || {}), lead_score: call.lead_score ?? _ca.lead_score, urgency_score: call.urgency_score ?? _ca.urgency_score, call_intent: call.call_intent ?? _ca.call_intent, call_summary: call.call_summary ?? _ca.call_summary, buying_signal: call.buying_signal ?? _ca.buying_signal, sentiment: call.sentiment ?? _ca.sentiment ?? _ca.user_sentiment, caller_name: call.caller_name ?? _ca.caller_name, appointment_booked: call.appointment_booked ?? _ca.appointment_booked, callback_requested: call.callback_requested ?? _ca.callback_requested, service_requested: call.service_requested ?? _ca.service_requested, action_items: call.action_items ?? _ca.action_items, is_existing_customer: call.is_existing_customer ?? _ca.is_existing_customer };
                return (
                  <tr key={call._id} onClick={() => navigate(`/calls/${call.call_id}`)}
                    className="border-b border-navy-600/50 hover:bg-navy-700 cursor-pointer transition-colors" data-testid={`admin-call-row-${call.call_id}`}>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <Building2 size={12} className="text-slate-500" />
                        <span className="text-xs text-accent-blue">{companyMap[call.company_id] || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm font-medium text-white">{a.caller_name || 'Ukjent'}</td>
                    <td className="px-4 py-3.5 text-sm text-slate-400 font-mono">{call.from_number}</td>
                    <td className="px-4 py-3.5"><span className="text-xs px-2 py-1 rounded bg-navy-700 text-slate-300">{a.call_intent || '—'}</span></td>
                    <td className="px-4 py-3.5"><span className={`text-sm font-mono font-medium ${(a.lead_score||0) >= 80 ? 'text-accent-green' : (a.lead_score||0) >= 50 ? 'text-accent-yellow' : 'text-slate-400'}`}>{a.lead_score||0}</span></td>
                    <td className="px-4 py-3.5"><span className={`text-sm font-mono ${(a.urgency_score||0) >= 7 ? 'text-accent-red' : 'text-slate-400'}`}>{a.urgency_score||0}/10</span></td>
                    <td className="px-4 py-3.5 text-sm text-slate-400 font-mono">{formatDuration(call.duration_seconds)}</td>
                    <td className="px-4 py-3.5 text-sm text-slate-400">{fmt(call.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-navy-600">
            <span className="text-xs text-slate-500">Side {page} av {pages}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page<=1} className="p-1.5 rounded-lg border border-navy-600 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page>=pages} className="p-1.5 rounded-lg border border-navy-600 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
